import React from 'react';
import { FaShareNodes } from 'react-icons/fa6';
import { MdContentCopy } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ModalLayout from '../layouts/ModalLayout';
import IconBadge from '../icons/IconBadge';
import Button from '../buttons/Button';
import { COMMUNITY_URL } from '../../../constants';

interface ShareAffiliateLinkModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  affiliateCode: string;
}

export default function ShareAffiliateLinkModal({
  open,
  setOpen,
  affiliateCode,
}: ShareAffiliateLinkModalProps): JSX.Element {
  const { t } = useTranslation();
  
  const affiliateLink = `${COMMUNITY_URL}/onboarding?code=${affiliateCode}`;

  const copyCodeToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(affiliateCode);
      toast.success(t('toast.success.copiedToClipboard'));
    } catch (err) {
      console.error('Failed to copy code:', err);
      toast.error('Failed to copy code to clipboard');
    }
  };

  const copyLinkToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(affiliateLink);
      toast.success(t('toast.success.copiedToClipboard'));
    } catch (err) {
      console.error('Failed to copy link:', err);
      toast.error('Failed to copy link to clipboard');
    }
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 mx-6 flex w-full min-w-[450px] max-w-[600px] flex-col rounded-[20px] bg-secondary-50 px-10 py-6 pb-4 pt-5"
    >
      <div className="mb-6 flex w-full items-center">
        <IconBadge icon={FaShareNodes} />
        <div className="ml-4 flex w-full flex-col">
          <h1 className="text-2xl">
            {t('page.affiliate.modal.title')}
          </h1>
          <p className="text-gray-600">
            {t('page.affiliate.modal.subtitle')}
          </p>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            {t('page.affiliate.modal.codeLabel')}
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="flex-1 rounded-full border border-primary bg-secondary-50 px-4 py-2 text-center font-mono text-lg font-semibold"
              value={affiliateCode}
              readOnly
            />
            <Button
              variant="secondary"
              onClick={copyCodeToClipboard}
              className="rounded-full p-3"
            >
              <MdContentCopy className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            {t('page.affiliate.modal.linkLabel')}
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="flex-1 overflow-x-scroll rounded-full border border-primary bg-secondary-50 px-4 py-2 text-sm"
              value={affiliateLink}
              readOnly
            />
            <Button
              variant="secondary"
              onClick={copyLinkToClipboard}
              className="rounded-full p-3"
            >
              <MdContentCopy className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </ModalLayout>
  );
}